import { Command } from "cmdk"
import { RefObject, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import useLayerStack from "@hooks/useLayerStack"

import Overlay from "@particles/Overlay/Overlay"

import { AppSearch } from "@organisms"

import styles from "./SearchModal.module.scss"
import { SearchModalProps } from "./SearchModal.types"

export default function SearchModal(props: SearchModalProps) {
    const { isOpen, onOpenChange } = props
    const [searchScope, setSearchScope] = useState<ObjectsNames>()
    const closeModal = () => onOpenChange(false)

    useEffect(() => {
        if (!isOpen) {
            setSearchScope(undefined)
        }
    }, [isOpen])

    const ref = useRef<HTMLDivElement>(null)

    const openModal = (event: CustomEvent<{ scope: ObjectsNames }>) => {
        setSearchScope(event.detail.scope)

        onOpenChange(true)
    }

    useEffect(() => {
        window.addEventListener("openSearchModal", openModal as EventListener)

        return () => {
            window.removeEventListener("openSearchModal", openModal as EventListener)
        }
    }, [])

    const { overlayZIndex, surfaceZIndex } = useLayerStack(isOpen)

    return createPortal(
        <SearchModalPortalContent
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            closeModal={closeModal}
            modalRef={ref}
            searchScope={searchScope}
            overlayZIndex={overlayZIndex}
            surfaceZIndex={surfaceZIndex}
        />,
        document.body,
    )
}

interface SearchModalPortalContentProps extends SearchModalProps {
    closeModal: () => void
    modalRef: RefObject<HTMLDivElement>
    searchScope?: ObjectsNames
    overlayZIndex: number
    surfaceZIndex: number
}

function SearchModalPortalContent(props: SearchModalPortalContentProps) {
    const { isOpen, onOpenChange, closeModal, modalRef, searchScope, overlayZIndex, surfaceZIndex } = props

    return (
        <div
            ref={modalRef}
            className={styles.base}
            style={{ zIndex: surfaceZIndex }}
            aria-hidden={!isOpen}
            tabIndex={-1}
        >
            <Overlay isActive={isOpen} style={{ zIndex: overlayZIndex }} onClick={closeModal} />
            <Command.Dialog
                open={isOpen}
                label="Global Command Menu"
                onOpenChange={onOpenChange}
                className={styles.dialog}
                container={modalRef.current ?? undefined}
                loop={true}
                shouldFilter={false}
            >
                <AppSearch closeSearchModal={closeModal} searchScope={searchScope} />
            </Command.Dialog>
        </div>
    )
}
